import React, { useCallback, useEffect, useState } from 'react';
import { Box, Switch, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { io, Socket } from 'socket.io-client';
import api from '../../services/api';
import { SOCKET_URL } from '../../config/api';
import { useI18n } from '../../i18n/useI18n';

type Props = {
  machineCode: string;
  userId?: string;
};

const ACCENT = '#0f3460';

const ControlPageUiToggle: React.FC<Props> = ({ machineCode, userId }) => {
  const { pages: t } = useI18n();
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const endpoint = userId
    ? `/admin/users/${encodeURIComponent(userId)}/machines/${encodeURIComponent(machineCode)}/control-page-ui`
    : `/machines/${encodeURIComponent(machineCode)}/control-page-ui`;

  const load = useCallback(async () => {
    try {
      const res = await api.get(endpoint);
      setEnabled(res.data?.controlPageUiEnabled !== false);
    } catch {
      setMessage(t.deviceMgmt.controlUiLoadError);
    }
  }, [endpoint, t.deviceMgmt.controlUiLoadError]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const socket: Socket = io(SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
    });
    const mc = String(machineCode).trim().toUpperCase();
    socket.on('connect', () => socket.emit('join-machine-room', mc));
    socket.on('control-page:ui-enabled-changed', (data) => {
      if (String(data?.machineCode || '').toUpperCase() !== mc) return;
      if (typeof data?.controlPageUiEnabled === 'boolean') {
        setEnabled(data.controlPageUiEnabled);
      }
    });
    return () => {
      socket.emit('leave-machine-room', mc);
      socket.disconnect();
    };
  }, [machineCode]);

  const onToggle = async (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setSaving(true);
    setMessage('');
    const prev = enabled;
    setEnabled(checked);
    try {
      const res = await api.patch(endpoint, {
        controlPageUiEnabled: checked,
        source: userId ? 'admin' : 'user',
      });
      setEnabled(res.data?.controlPageUiEnabled !== false);
      setMessage(t.deviceMgmt.controlUiSaveSuccess);
    } catch (error: any) {
      setEnabled(prev);
      setMessage(error?.response?.data?.message || t.deviceMgmt.controlUiSaveError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        mb: 2,
        p: { xs: 1.25, sm: 1.75 },
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: alpha(ACCENT, 0.12),
        bgcolor: alpha(ACCENT, 0.03),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.92rem', sm: '1rem' }, color: ACCENT }}>
          {t.deviceMgmt.controlUiTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mt: 0.25 }}>
          {t.deviceMgmt.controlUiHint}
        </Typography>
        {message ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {message}
          </Typography>
        ) : null}
      </Box>
      <Switch
        checked={enabled}
        onChange={onToggle}
        disabled={saving}
        color="primary"
      />
    </Box>
  );
};

export default ControlPageUiToggle;
